import React from "react";
import Input from "src/components/Shared/Forms/Inputs/Input";
import Toggle from "src/components/Shared/Forms/Toggles/Toggle";
import ToggleHeader from "src/components/Shared/Forms/Toggles/ToggleHeader";
import InformationAlert from "src/components/Shared/Alerts/InformationAlert";
import Button from "src/components/Shared/Buttons/Button";
import { H3 } from "src/components/Shared/Text/Headers";
import { formatCurrency } from "src/helpers";
import Editor from "src/components/Shared/Forms/Inputs/Editor/Editor";

const PlanDefaultForm = ({ formData, setFormData }) => {
  const calculateYearlyAmount = (monthlyAmount, discountPercentage) => {
    const yearlyAmount = monthlyAmount * 12;
    const discount = (yearlyAmount * discountPercentage) / 100;
    return {
      yearlyAmount,
      discount,
      monthlyAmount,
      discountPercentage,
      discountedAmount: yearlyAmount - discount,
    };
  };

  return (
    <div className="animate-fade-in flex w-full flex-wrap gap-x-4 gap-y-3">
      <div className="w-full">
        <Input
          inline={true}
          name="plan-name"
          label="Name"
          value={formData?.name || ""}
          onChange={(e) => {
            setFormData((prevData) => ({ ...prevData, name: e.target.value }));
          }}
        />
      </div>
      <div className="w-full">
        <Editor
          inline={true}
          label="Description"
          value={formData?.description || ""}
          onChange={(e) => {
            setFormData((prevData) => ({ ...prevData, description: e.target.value }));
          }}
        />
      </div>
      <div className="w-full px-2">
        <ToggleHeader
          title="Free Plan"
          subtitle="Offer free plan"
          position="left">
          <Toggle
            checked={formData?.pricing?.type === "free"}
            onChange={() => {
              setFormData((prevData) => {
                const isCurrentlyFree = prevData?.pricing?.type === "free";
                return {
                  ...prevData,
                  pricing: {
                    ...prevData?.pricing,
                    type: isCurrentlyFree ? "standard" : "free",
                    ...(isCurrentlyFree && {
                      rates: prevData?.pricing?.rates?.length
                        ? prevData.pricing.rates
                        : [
                            { interval: "month", amount: 0, discount_percentage: 0 },
                            { interval: "year", amount: 0, discount_percentage: 0 },
                          ],
                    }),
                  },
                };
              });
            }}
          />
        </ToggleHeader>
      </div>
      {formData?.pricing?.type !== "free" &&
        formData?.pricing?.rates?.map((price, index) => (
          <div
            key={index}
            className="flex w-full flex-col space-y-4 px-2">
            {price?.interval === "month" && (
              <div className="flex w-full flex-col space-y-2">
                <Input
                  wrapperClass="flex-shrink flex-grow"
                  type="number"
                  inline={true}
                  name={`${price?.interval}-amount`}
                  label="Monthly Amount"
                  value={price?.amount || ""}
                  onChange={(e) => {
                    const newMonthlyAmount = parseFloat(e.target.value) || 0;
                    setFormData((prevData) => ({
                      ...prevData,
                      pricing: {
                        ...prevData.pricing,
                        rates: prevData.pricing.rates.map((item) => (item?.interval === "month" ? { ...item, amount: newMonthlyAmount } : item?.interval === "year" ? { ...item, amount: newMonthlyAmount * 12 } : item)),
                      },
                    }));
                  }}
                />
              </div>
            )}
            {price?.interval === "year" && (
              <div className="flex w-full flex-col space-y-2">
                <Input
                  wrapperClass="flex-shrink flex-grow"
                  type="number"
                  inline={true}
                  name={`${price?.interval}-discount`}
                  label="Yearly Discount (%)"
                  value={price?.discount_percentage || ""}
                  onChange={(e) => {
                    const newDiscount = parseFloat(e.target.value) || 0;
                    setFormData((prevData) => ({
                      ...prevData,
                      pricing: {
                        ...prevData.pricing,
                        rates: prevData.pricing.rates.map((item) => (item?.interval === "year" ? { ...item, discount_percentage: newDiscount } : item)),
                      },
                    }));
                  }}
                />
              </div>
            )}
          </div>
        ))}

      {formData?.pricing?.type !== "free" && formData?.pricing?.rates?.some((rate) => rate.interval === "month" && rate.amount > 0) && (
        <div className=" w-full px-2">
          {formData?.pricing?.rates?.map((rate) => {
            if (rate.interval === "month" && rate.amount > 0) {
              const { yearlyAmount, discountPercentage, discountedAmount, monthlyAmount } = calculateYearlyAmount(rate.amount, formData.pricing.rates.find((r) => r.interval === "year")?.discount_percentage || 0);
              return (
                <div
                  key={rate.interval}
                  className="text-sm">
                  <InformationAlert
                    type={"info"}
                    size="lg"
                    content={
                      <>
                        <H3 className="mb-1 mt-0 !text-lg">Price Summary</H3>
                        <div className="space-y-1">
                          <div>
                            Monthly Amount: <span className="font-semibold">{formatCurrency(monthlyAmount)}</span>
                          </div>
                          <div>
                            Yearly Amount: <span className={`font-semibold ${discountPercentage > 0 && "line-through"}`}>{formatCurrency(yearlyAmount)}</span> {discountPercentage > 0 && <span className="font-semibold">{formatCurrency(discountedAmount)}</span>}
                            {discountPercentage > 0 && (
                              <Button
                                version="default"
                                className="ml-2 inline-flex !h-auto rounded-md border border-green-300 px-2 pb-0.5 pt-1 text-xs font-semibold text-green-600">
                                Save {discountPercentage}%
                              </Button>
                            )}
                          </div>
                        </div>
                      </>
                    }
                  />
                </div>
              );
            }
            return null;
          })}
        </div>
      )}
      <div className="w-full px-2 pb-4">
        <ToggleHeader
          title="Free trial"
          subtitle="Offer free trial"
          position="left">
          <Toggle
            checked={formData?.free_trial?.status}
            onChange={() => {
              setFormData((prevData) => ({
                ...prevData,
                free_trial: {
                  ...prevData?.free_trial,
                  status: !prevData?.free_trial?.status,
                  days: 0,
                },
              }));
            }}
          />
        </ToggleHeader>
        {formData?.free_trial?.status && (
          <Input
            type="number"
            inline={true}
            name="free-trial"
            label="Free trial (in days)"
            value={formData?.free_trial?.days || ""}
            onChange={(e) => {
              setFormData((prevData) => ({ ...prevData, free_trial: { ...prevData.free_trial, days: e.target.value } }));
            }}
          />
        )}
      </div>
    </div>
  );
};

export default PlanDefaultForm;
